/**
 * PR-23 — Partner application + account store (frontend, localStorage).
 * Mirrors the demo-grade persistence used by user profile / partner roles.
 * In production these records live in Supabase under `partner_accounts`.
 */
import { trackEvent } from "@/lib/audit";

const APPS_KEY = "vs:partner_applications";
const ACCOUNTS_KEY = "vs:partner_accounts";
const SESSION_KEY = "vs:partner_session";

export type PartnerStatus = "pending_review" | "approved" | "rejected" | "suspended";

export type PartnerModule =
  | "executive_dashboard"
  | "revenue_dashboard"
  | "sponsored_polls"
  | "campaign_performance"
  | "audience_analytics"
  | "geo_insights"
  | "reward_redemption"
  | "roi_reports"
  | "export_center"
  | "billing"
  | "support";

export const ALL_PARTNER_MODULES: PartnerModule[] = [
  "executive_dashboard",
  "revenue_dashboard",
  "sponsored_polls",
  "campaign_performance",
  "audience_analytics",
  "geo_insights",
  "reward_redemption",
  "roi_reports",
  "export_center",
  "billing",
  "support",
];

export interface PartnerApplication {
  id: string;
  company_name: string;
  company_slug: string;
  contact_name: string;
  corporate_email: string;
  phone: string;
  country: string;
  industry: string;
  company_type: string;
  role: string;
  requested_modules: PartnerModule[];
  nda_accepted_at: string | null;
  status: PartnerStatus;
  submitted_at: string;
  approved_at?: string | null;
  approved_by?: string | null;
  approved_modules?: PartnerModule[];
  read_only: boolean;
  demo_mode: boolean;
  expires_at?: string | null;
  tenant_id?: string | null;
  password?: string; // demo only; never use in production
}

export interface PartnerSession {
  partner_id: string;
  company_name: string;
  company_slug: string;
  role: string;
  status: PartnerStatus;
  approved_modules: PartnerModule[];
  demo_mode: boolean;
  tenant_id?: string | null;
  signed_in_at: string;
}

function readList(key: string): PartnerApplication[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as PartnerApplication[]) : [];
  } catch {
    return [];
  }
}

function writeList(key: string, list: PartnerApplication[]) {
  if (typeof window === "undefined") return;
  localStorage.setItem(key, JSON.stringify(list));
}

function slugify(name: string) {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function makeId() {
  return `partner_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function listApplications(): PartnerApplication[] {
  return readList(APPS_KEY);
}

export function listAccounts(): PartnerApplication[] {
  return readList(ACCOUNTS_KEY);
}

export function submitApplication(
  data: Omit<PartnerApplication, "id" | "company_slug" | "status" | "submitted_at" | "read_only" | "demo_mode">,
): PartnerApplication {
  const apps = listApplications();
  const app: PartnerApplication = {
    ...data,
    id: makeId(),
    company_slug: slugify(data.company_name),
    status: "pending_review",
    submitted_at: new Date().toISOString(),
    read_only: true,
    demo_mode: false,
  };
  apps.push(app);
  writeList(APPS_KEY, apps);
  void trackEvent("partner_application_submitted", {
    partner_id: app.id,
    company: app.company_name,
    country: app.country,
  });
  return app;
}

export function approveApplication(
  id: string,
  opts: { modules: PartnerModule[]; tenant_id?: string; demo?: boolean; expires_at?: string | null } = { modules: [] },
): PartnerApplication | null {
  const apps = listApplications();
  const idx = apps.findIndex((a) => a.id === id);
  if (idx < 0) return null;
  const updated: PartnerApplication = {
    ...apps[idx],
    status: "approved",
    approved_at: new Date().toISOString(),
    approved_by: "super_admin",
    approved_modules: opts.modules.length > 0 ? opts.modules : apps[idx].requested_modules,
    tenant_id: opts.tenant_id ?? `tenant_${apps[idx].company_slug}`,
    demo_mode: Boolean(opts.demo),
    expires_at: opts.expires_at ?? null,
    read_only: true,
  };
  apps[idx] = updated;
  writeList(APPS_KEY, apps);
  const accounts = listAccounts().filter((a) => a.id !== id);
  accounts.push(updated);
  writeList(ACCOUNTS_KEY, accounts);
  void trackEvent("partner_application_approved", { partner_id: id, company: updated.company_name });
  if (updated.demo_mode) {
    void trackEvent("partner_demo_access_granted", { partner_id: id, tenant_id: updated.tenant_id });
  }
  return updated;
}

export function rejectApplication(id: string, reason?: string): void {
  const apps = listApplications();
  const idx = apps.findIndex((a) => a.id === id);
  if (idx < 0) return;
  apps[idx] = { ...apps[idx], status: "rejected" };
  writeList(APPS_KEY, apps);
  void trackEvent("partner_application_rejected", { partner_id: id, reason: reason ?? "unspecified" });
}

export function suspendAccount(id: string): void {
  const accounts = listAccounts();
  const idx = accounts.findIndex((a) => a.id === id);
  if (idx < 0) return;
  accounts[idx] = { ...accounts[idx], status: "suspended" };
  writeList(ACCOUNTS_KEY, accounts);
  void trackEvent("partner_application_suspended", { partner_id: id });
}

export function updateAccountPermissions(id: string, modules: PartnerModule[]): void {
  const accounts = listAccounts();
  const idx = accounts.findIndex((a) => a.id === id);
  if (idx < 0) return;
  accounts[idx] = { ...accounts[idx], approved_modules: modules };
  writeList(ACCOUNTS_KEY, accounts);
  void trackEvent("partner_permissions_updated", { partner_id: id, modules });
}

/** Demo-only login: matches by email + (optional) password. */
export function partnerLogin(email: string, password?: string): PartnerSession | null {
  const account = listAccounts().find(
    (a) => a.corporate_email.toLowerCase() === email.toLowerCase() && a.status === "approved",
  );
  if (!account) {
    void trackEvent("partner_login_failed", { email, reason: "no_approved_account" });
    return null;
  }
  if (account.password && password && account.password !== password) {
    void trackEvent("partner_login_failed", { email, reason: "bad_password" });
    return null;
  }
  const session: PartnerSession = {
    partner_id: account.id,
    company_name: account.company_name,
    company_slug: account.company_slug,
    role: account.role,
    status: account.status,
    approved_modules: account.approved_modules ?? [],
    demo_mode: account.demo_mode,
    tenant_id: account.tenant_id ?? null,
    signed_in_at: new Date().toISOString(),
  };
  if (typeof window !== "undefined") {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }
  void trackEvent("partner_login_success", {
    partner_id: account.id,
    company: account.company_name,
    demo: account.demo_mode,
  });
  return session;
}

export function partnerLogout(): void {
  if (typeof window === "undefined") return;
  const raw = localStorage.getItem(SESSION_KEY);
  if (raw) {
    try {
      const s = JSON.parse(raw) as PartnerSession;
      void trackEvent("partner_logout", { partner_id: s.partner_id });
    } catch { /* ignore */ }
  }
  localStorage.removeItem(SESSION_KEY);
}

export function getPartnerSession(): PartnerSession | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as PartnerSession) : null;
  } catch {
    return null;
  }
}

/** Seed canonical demo accounts so SuperAdmin/QA can test approval flow. */
export function ensureDemoSeed(): void {
  if (typeof window === "undefined") return;
  if (localStorage.getItem("vs:partner_seed_v1")) return;
  const seeds: PartnerApplication[] = [
    {
      id: "partner_univision_demo",
      company_name: "Univision",
      company_slug: "univision",
      contact_name: "Maria Gomez",
      corporate_email: "partners@univision.com",
      phone: "+1 305 555 0100",
      country: "US",
      industry: "Broadcast Media",
      company_type: "Enterprise",
      role: "VP Digital",
      requested_modules: ALL_PARTNER_MODULES,
      approved_modules: ALL_PARTNER_MODULES,
      nda_accepted_at: new Date().toISOString(),
      status: "approved",
      submitted_at: new Date().toISOString(),
      approved_at: new Date().toISOString(),
      approved_by: "super_admin",
      read_only: true,
      demo_mode: true,
      tenant_id: "tenant_univision",
      password: "univision2026",
    },
  ];
  writeList(APPS_KEY, seeds);
  writeList(ACCOUNTS_KEY, seeds);
  localStorage.setItem("vs:partner_seed_v1", "1");
}